//! TODO: COMBINE (this) OnlineLayout and ContactCenterLayout in to a single layout
//! ----------------------------------------------------------------

import React, { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
	FormProvider,
	MainLayout,
	Section,
	ProgressStepper,
} from "@arp/react-ndbx";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

//** Context */
import usePreQualification from "../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../hooks/usePreQualificationPages.ts";
import useNewBusiness from "../../hooks/useNewBusiness.ts";
import useSharedData from "../../hooks/useSharedData.ts";
import useAuth from "../../hooks/useAuth.ts";
import { useGetReferenceData } from "../../hooks/dataHooks/useGetReferenceData.js";
import { PaymentContext } from "../../context/BraintreeContext.jsx";

//** Hooks */
import { useCreateQuoteMutation } from "../../hooks/dataHooks/useCreateQuoteMutation.js";
import { useUpdateQuoteMutation } from "../../hooks/dataHooks/useUpdateQuoteMutation.js";
import { useUpdateContactDetails } from "../../hooks/dataHooks/useUpdateContactDetailsMutation.js";
import { useUpdatePageIndicator } from "../../hooks/dataHooks/useUpdatePageIndicatorMutation.js";

//** Footer */
import { Footer } from "../../components/pageLevel/index.js";

//** Utilities */
import { Application } from "../../config/const/enums.js";
import { isQuotePages, pageName } from "../../config/pageInfo/pageNames.js";
import { Header } from "../../components/pageLevel/header/index.js";
import { validateContactDetails } from "../../utilities/validateContactDetails.js";
import _ from "lodash";

import {
	LIFE_INSURANCE_MIN,
	LIFE_INSURANCE_MAX,
	CRITICAL_ILLNESS_MIN,
	CRITICAL_ILLNESS_MAX,
	PERMANENT_DISABILITY_MIN,
	PERMANENT_DISABILITY_MAX,
	sharedValues,
} from "../../schema/sharedConstValues.js";
import { PATH_ONLINE_SUB } from "../../routes/paths.js";
import { isAdditionalPolicySameAsPrimary } from "../../utilities/preQualificationUtils.js";

import * as Sentry from "@sentry/react";

//----------------------------------------------------------------

export default function SharedLayout({ isContactCenter = false }) {
	const isOnline = !isContactCenter;

	const { state, funcs, progressItems, initializing, initialized } =
		usePreQualificationPages();

	const { getOktaAccessToken } = useAuth();

	const [pageCurrentPage, setPageCurrentPage] = useState(null);
	const [showProgress, setShowProgress] = useState(false);
	const [secondPersonAdded, setSecondPersonAdded] = useState(false);

	const [requireContactDetailsUpdate, setRequireContactDetailsUpdate] =
		useState(false);

	//* Disable the payment button after it is clicked once and until we get a valid response
	const [shouldDisableDoublePayment, setShouldDisableDoublePayment] =
		useState(false);

	//* Ensure that they form data has updated the dataSet before
	//* trying to call the APIs that need this data.
	const [isFormDataUpdated, setIsFormDataUpdated] = useState(false);

	//** These useStates are passed to the validation scripts */
	//* manages the validation of the yes/no radio buttons on the Select Quote page.
	const [personOneTraumaQuestion, setPersonOneTraumaQuestion] = useState(false);

	//* manages the validation of the yes/no radio buttons on the Policy Outcome page.
	const [outcomeTraumaQuestion, setOutcomeTraumaQuestion] = useState(false);

	//* manages the validation of the occupation list.
	const [personOneOccupationList, setPersonOneOccupationList] = useState(false);

	//* min and max age range - this is used for dynamic validation
	//* Life Cover
	const [personOneMinLifeCover, setPersonOneMinLifeCover] = useState();
	const [personOneMaxLifeCover, setPersonOneMaxLifeCover] = useState();

	//* Critical Illness Cover
	const [personOneMinCICover, setPersonOneMinCICover] = useState();
	const [personOneMaxCICover, setPersonOneMaxCICover] = useState();

	//* Permanently Unable to Work Cover
	const [personOneMinPUWCover, setPersonOneMinPUWCover] = useState();
	const [personOneMaxPUWCover, setPersonOneMaxPUWCover] = useState();

	//* Required for the Policy Outcome pages
	const [showApplyCheckbox, setShowApplyCheckbox] = useState(false);
	const [outcomeFirstName, setOutcomeFirstName] = useState("");
	const [upgradeChecked, setUpgradeCheckbox] = useState(false);

	//* The validation on the consent question on the contact page is dynamic
	const [doNotValidateConsent, setDoNotValidateConsent] = useState(true);

	//* Make the call to getReferenceData early to ensure that it is
	//* available to the application
	const { isLoading: refDataLoading } = useGetReferenceData();

	useEffect(() => {
		if (initialized === true) {
			setPageCurrentPage(state?.currentPage);
			setShowProgress(state?.currentPage?.showProgressBar);
		}
	}, [state]);

	const navigate = useNavigate();
	const location = useLocation();
	const { dataSet, setValues, funcSet, removeSecondApplicant } =
		usePreQualification();

	const { data: newBusinessData } = useNewBusiness();

	//** Shared data */
	const {
		updatingQuoteSummary,
		handleEventChange,
		openErrorModal,
		retrieveQuoteJourney,
		accessToken: quoteAccessToken,
	} = useSharedData();

	const [
		residentialMailingAddressAutoComplete,
		setResidentialMailingAddressAutoComplete,
	] = useState(false);

	const [
		policyMailingAddressAutoComplete,
		setPolicyMailingAddressAutoComplete,
	] = useState(true);

	const [paymentMethodTypeBankAccount, setPaymentMethodTypeBankAccount] =
		useState(false);

	const [additionalPolicyHolder, setAdditionalPolicyHolder] = useState(false);
	const [isAdditionalSameAsPrimary, setIsAdditionalSameAsPrimary] =
		useState(false);

	//----------------------------------------------------------------
	//* TODO: THERE MAY BE A BETTER WAY TO DO THIS.
	//* For now, this loads the age and cover limit values in to the correct arrays
	const dataReturned = sharedValues();

	//* Life Cover
	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMinLifeCover(
			LIFE_INSURANCE_MIN[dataSet?.personOne?.minAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.minAgeGroup, newBusinessData]);

	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMaxLifeCover(
			LIFE_INSURANCE_MAX[dataSet?.personOne?.maxAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.maxAgeGroup, newBusinessData]);

	//* Critical Illness Cover
	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMinCICover(
			CRITICAL_ILLNESS_MIN[dataSet?.personOne?.minAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.minAgeGroup, newBusinessData]);

	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMaxCICover(
			CRITICAL_ILLNESS_MAX[dataSet?.personOne?.maxAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.maxAgeGroup, newBusinessData]);

	//* Permanently Unable to Work Cover
	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMinPUWCover(
			PERMANENT_DISABILITY_MIN[dataSet?.personOne?.minAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.minAgeGroup, newBusinessData]);

	useEffect(() => {
		if (dataSet == null || dataReturned === false) return;
		setPersonOneMaxPUWCover(
			PERMANENT_DISABILITY_MAX[dataSet?.personOne?.maxAgeGroup]
		);
	}, [dataSet, dataSet?.personOne?.maxAgeGroup, newBusinessData]);

	//* Is the apply checkbox on the Policy Outcome displayed.
	useEffect(() => {
		if (newBusinessData == null) return;
		// setShowApplyCheckbox(newBusinessData?.covers?.anyExclusions || false);
		setShowApplyCheckbox(
			newBusinessData?.covers?.anyExclusionsOrLoadings || false
		);
	}, [newBusinessData]);
	//----------------------------------------------------------------

	//** Onload: Set the application type */
	useEffect(() => {
		const applicationType =
			isContactCenter === true
				? Application.CONTACT_CENTER
				: Application.ONLINE;
		initializing(applicationType); //Pages context
		funcSet.initializing(applicationType); //PreQual context
		setPersonOneTraumaQuestion(false);
		setOutcomeTraumaQuestion(false);
		setResidentialMailingAddressAutoComplete(true);
	}, []);

	//** */

	//----------------------------------------------------------------
	//* TEST - We want to check IF the application data and not the cover(s) has changed
	//* If it has, we need to call, updateQuote, if only the covers have changed, call updateCovers
	// const [isApplicationDataChanged, setIsApplicationDataChanged] =
	// 	useState(false);
	//* TEST

	const [currentData, setCurrentData] = useState(null);
	const [updateOktaUser, setUpdateOktaUser] = useState(false);

	const [pageMovingFrom, setPageMovingFrom] = useState(pageName.ACCEPT);
	const paymentContext = useContext(PaymentContext);
	const { paymentTransaction, isPaymentMade, paymentMethod, isGatewayError } =
		paymentContext;

	const [paymentInProcess, setPaymentInProcess] = useState(false);
	//* Navigation Section
	const onSubmit = async (formData) => {
		// ** If we have created a quote and we are back on either
		// ** the contact or personal info page, we need to check
		// ** if we have updated any of the fields that require
		// ** an update to Okta
		let localValue = updateOktaUser; //This allows us to retain state between state update.
		setPageMovingFrom(pageCurrentPage?.identifier);

		if (quoteAccessToken != null) {
			if (
				pageCurrentPage?.identifier === pageName.CONTACT ||
				pageCurrentPage?.identifier === pageName.PERSONAL
			) {
				setUpdateOktaUser(true);
				localValue = true;
			}
		}

		if (pageCurrentPage?.identifier === pageName.REVIEW_APPLICATION) {
			//* Ensure that the retrieveQuoteJourney is set to false.
			//* It may be true after a policy retrieval and it needs to be
			//* false when we land on the Outcome page to trigger
			//* a call to get the Interview Response API.
			handleEventChange({
				name: "retrieveQuoteJourney",
				value: false,
			});
		}

		// Check the additionalPolicy holder detail with the main applicant and if the same then show error and dont submit.
		if (pageCurrentPage?.identifier === pageName.PERSONAL) {
			if (isAdditionalPolicySameAsPrimary(formData)) {
				setIsAdditionalSameAsPrimary(true);
				return;
			} else {
				setIsAdditionalSameAsPrimary(false);
			}
		}

		//* Make the payment before we move to the next page.
		//* We do this because, we don't want to move off the Payment Details page
		//* if there are any errors.
		//! TODO: Figure out someway to know that we have completed the payments.
		if (
			pageCurrentPage?.identifier === isContactCenter
				? pageName.PP_CC_PAYMENT
				: pageName.PP_ONLINE_PAYMENT && paymentMethod != null
		) {
			setShouldDisableDoublePayment(true);
			const responseStatus = await paymentTransaction({
				accessToken: quoteAccessToken,
				requestData: formData,
				isContactCentre: isContactCenter,
			});
			if (responseStatus?.toString()?.startsWith(5)) {
				openErrorModal();
			}
			setShouldDisableDoublePayment(false);
			if (responseStatus !== 200) return;
			setPaymentInProcess(!isPaymentMade);
		}

		//* All okay? Validate the page and go forth.
		//* This moves the current page forward to the next page.
		await funcs.validateAndProceed();
		setShouldDisableDoublePayment(false);
		await setValues(formData, pageCurrentPage?.identifier, localValue);
		setIsFormDataUpdated(true);

		// }
	};
	//************************************** */

	const {
		mutate: updateContactDetailsMutate,
		isSuccess: updateContactDetailsIsSuccess,
		data: updateContactDetailsData,
		error: updateContactDetails,
	} = useUpdateContactDetails();

	useEffect(() => {
		const callCreateUpdateAPI = async () => {
			if (dataSet == null || isGatewayError === true) return;

			if (
				pageCurrentPage?.identifier === pageName.QUOTE &&
				pageCurrentPage?.valid === false &&
				updatingQuoteSummary === false &&
				dataSet?.dataUpdating === false
			) {
				dataSet.quoteCreated = true;
				await createUpdateQuote({ updateData: dataSet });
			}

			//* although we are calling update contact details for payments
			//* We have already moved on to the next page, so we can show
			//* the loading and spinner
			if (
				(pageCurrentPage?.identifier === pageName.PP_CC_PAYMENT ||
					pageCurrentPage?.identifier === pageName.PP_ONLINE_PAYMENT) &&
				requireContactDetailsUpdate === true &&
				isFormDataUpdated === true
			) {
				//* Ensure we have all valid values before
				//* we try to update the contact details
				const validDataUpdate = await validateContactDetails(dataSet);

				if (validDataUpdate === true) {
					dataSet.jwtToken = quoteAccessToken;
					updateContactDetailsMutate(dataSet);
					setRequireContactDetailsUpdate(false);
				}
			} else if (pageCurrentPage?.identifier === pageName.PP_CONTACT) {
				setRequireContactDetailsUpdate(true);
				setIsFormDataUpdated(false);
			}
		};

		callCreateUpdateAPI();
	}, [dataSet, dataSet?.dataUpdating]);

	const onBackButton = async () => {
		await funcs.validateOnBack();
	};

	const onHandleSecondPerson = async () => {
		setSecondPersonAdded(!secondPersonAdded);
		removeSecondApplicant();
	};

	//----------------------------------------------------------------
	const {
		mutate: createMutate,
		isSuccess,
		data: mutateData,
		error: mutError,
	} = useCreateQuoteMutation();

	const {
		mutate: updateMutate,
		isSuccess: isUpdateSuccess,
		data: mutUpdateData,
		error: mutUpdateError,
	} = useUpdateQuoteMutation();

	const mutateCreateQuote = async (updateData) => {
		//* Create the quote - with an Okta JWT token
		let oktaAccessToken = await getOktaAccessToken();
		const { accessToken } = { ...oktaAccessToken };
		createMutate({
			updateData: updateData,
			oktaToken: accessToken,
		});
	};

	const createUpdateQuote = async ({ updateData }) => {
		//* Check if we need to update/mutate the quote
		const areDeepEqual = _.isEqual(updateData, currentData);
		let quoteNumber = mutateData?.quoteInformation?.quoteNumber;
		let jwtToken = mutateData?.quoteInformation?.jwtToken;
		// On retrieval, mutateData won't be available, so set the quoteNumber from updateData, and accessToken from sharedData
		if (retrieveQuoteJourney === true) {
			quoteNumber = updateData?.quoteNumber;
			jwtToken = quoteAccessToken;
		}

		if (quoteNumber == null && retrieveQuoteJourney === false) {
			//* Create the quote
			isContactCenter === true
				? mutateCreateQuote(updateData)
				: createMutate(updateData);

			setUpdateOktaUser(false);
		}
		if (quoteNumber != null && areDeepEqual === false) {
			//* Update quote
			updateData.jwtToken = jwtToken;
			updateMutate(updateData);
			setUpdateOktaUser(false);
		}

		//* set the current data so we can compare later
		setCurrentData(updateData);
		return true;
	};

	//----------------------------------------------------------------
	//** We need to check to see if we are to validate
	//** the set of Trauma Questions
	//*/
	const onHandleValidateTrauma = async (val, person) => {
		switch (person) {
			case "personOne":
				setPersonOneTraumaQuestion(val);
				break;
			case "personTwo":
				// setPersonTwoTraumaQuestion(val);
				break;
			default:
				break;
		}
	};

	const onHandleValidateOutcomeTrauma = async (val) => {
		setOutcomeTraumaQuestion(val);
	};

	const onHandleValidateOccupation = async (val, person) => {
		switch (person) {
			case "personOne":
				setPersonOneOccupationList(val);
				break;
			case "personTwo":
				// setPersonTwoOccupationList(val);
				break;
			default:
				break;
		}
	};

	const onHandleMailingAddress = async (val) => {
		const { name, value } = { ...val };
		switch (name) {
			case "residentialAddressAutoComplete":
				setResidentialMailingAddressAutoComplete(value);
				break;
			case "policyMailingAddressAutoComplete":
				setPolicyMailingAddressAutoComplete(value);
				break;
			default:
				break;
		}
	};

	const onHandlePaymentMethodType = async (val) => {
		setPaymentMethodTypeBankAccount(val === "bankacc" ? true : false);
	};

	const onHandleCCUpgradePolicy = async (val) => {
		setUpgradeCheckbox(val);
	};

	const onHandleApplyCheckbox = async (val) => {
		// setShowApplyCheckbox(true);
	};

	const onHandleAdditionalPolicyHolder = async (val) => {
		setAdditionalPolicyHolder(val);
	};
	//----------------------------------------------------------------

	//** When the  current page state  change */
	//** Navigate to that page */
	useEffect(() => {
		try {
			isContactCenter ? contactCenterNavigation() : onlineNavigation();
		} catch (ex) {
			console.error(ex);
		}
	}, [pageCurrentPage?.routePath]);

	const contactCenterNavigation = () => {
		navigate(pageCurrentPage?.routePath);
	};

	const onlineNavigation = () => {
		const queryParams = new URLSearchParams(location.search);
		const newSearch = queryParams.toString();
		if (pageCurrentPage?.routePath) {
			navigate({
				pathname: pageCurrentPage?.routePath,
				search:
					pageCurrentPage?.routePath === PATH_ONLINE_SUB.accept
						? `?${newSearch}`
						: "",
			});
		}
	};

	//----------------------------------------------------------------
	async function onFocus(e) {
		e.preventDefault();
		clearErrors(e.target.name);
	}

	const onError = (errorData) => {
		console.error(errorData);
	};

	//----------------------------------------------------------------

	/**
	 * Set page indicator on interview review page and contact details page
	 */
	const { mutateAsync: updatePageIndicatorMutate } = useUpdatePageIndicator();

	const setPageIndicator = async (payload) => {
		await updatePageIndicatorMutate(payload)
			.then(() => funcs.confirmPageIndicatorSet())
			.catch((err) => console.log("Error: ", err));
	};

	useEffect(() => {
		// Check if there is a page indicator code to be sent to eLife, and make sure it's not set already
		if (
			!pageCurrentPage?.pageIndicator ||
			!pageCurrentPage?.pageIndicator.startsWith("L")
		)
			return;

		const payload = {
			code: pageCurrentPage?.pageIndicator,
			accessToken: quoteAccessToken,
		};
		setPageIndicator(payload);
	}, [pageCurrentPage]);
	/**
	 * End of Set page indicator
	 */

	//** Check if we need to validate the consent */
	//** We don't need validation if it is a CC flow or quote created in a CC */
	useEffect(() => {
		if (dataSet?.["personOne"] == null) return;

		setDoNotValidateConsent(
			retrieveQuoteJourney === true &&
				dataSet?.["personOne"]?.callbackConsent == null
		);
	}, [dataSet]);

	//! NOTE: The items passed in the context 0f the useForm
	//! are passed to the yup validation scripts
	//! they are use to determine if a field is visible on the form
	//! and therefor needs to be validated (or not)

	const methods = useForm({
		dataSet,
		resolver:
			pageCurrentPage?.schema && refDataLoading === false
				? yupResolver(
						secondPersonAdded === false
							? pageCurrentPage?.schema
							: pageCurrentPage?.dualSchema
					)
				: null,
		mode: "all",
		reValidateMode: "all",
		context: {
			personContext: secondPersonAdded,
			validateOutcomeTraumaQuestion: outcomeTraumaQuestion,
			personOne_validateTraumaQuestion: personOneTraumaQuestion,
			personOne_validateOccupationList: personOneOccupationList,
			personOne_minLifeCover: personOneMinLifeCover,
			personOne_maxLifeCover: personOneMaxLifeCover,
			personOne_minCICover: personOneMinCICover,
			personOne_maxCICover: personOneMaxCICover,
			personOne_minPUWCover: personOneMinPUWCover,
			personOne_maxPUWCover: personOneMaxPUWCover,
			isResidentialMailingAddressAutoComplete:
				residentialMailingAddressAutoComplete, //* Are we using the Autocomplete for Residential Address?
			isPolicyMailingAddressAutoComplete: policyMailingAddressAutoComplete, //* Are we using the Autocomplete for Mailing Address?
			isContactCenterApplication: isContactCenter,
			isPaymentMethodTypeBankAccount: paymentMethodTypeBankAccount, //* Use in the validation of Bank Account payments
			isShowApplyCheckbox: showApplyCheckbox, //* Used on the Policy Outcome validation
			currentUserFirstName: outcomeFirstName, //* Required for use in a dynamic validation
			upgradeChecked: upgradeChecked, //* Default to false as upgrade is only available for CC.
			isAdditionalPolicyHolder: additionalPolicyHolder, //* Used when there is additional policy holder added
			isConsentCheckRequired: isContactCenter || doNotValidateConsent === true, //* Is the consent question displayed and requires validation
		},
	});

	const { clearErrors } = methods;
	const { isDirty, isValid, isSubmitting, errors } = methods.formState;
	//----------------------------------------------------------------

	return (
		<>
			<Header
				controlIdentifier={
					isContactCenter ? "Contact Center header" : "Online header"
				}
				logoImage="https://www.allianz.com/content/dam/onemarketing/system/allianz-logo.svg"
				buttonTitle="Contact Us"
				contactUsImg="https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/images/woman-with-laptop-headset.svg"
			/>
			<FormProvider
				methods={methods}
				onSubmit={methods.handleSubmit(onSubmit, onError)}
				controlIdentifier={
					isContactCenter
						? "PreQual Contact Center Layout"
						: "PreQual Online Layout"
				}
				ariaLabel="Allianz Life Plan Quote"
			>
				<MainLayout
					maxWidth="lg"
					sx={{
						mt: 8,
						mb: 5,
						outline: "0px solid purple",
						minHeight: "100vh",
					}}
				>
					<>
						{showProgress ? (
							<Section
								id="progress-stepper"
								sx={{
									border: "0px solid green",
									minWidth: { lg: "1200px" },
									maxWidth: { lg: "1200px" },
									flexShrink: 1,
								}}
							>
								<ProgressStepper
									items={progressItems}
									activeStep={pageCurrentPage?.activeStep}
								/>
							</Section>
						) : null}

						{pageCurrentPage ? (
							<Outlet
								context={{
									isDirty: [isDirty],
									isValid: [isValid],
									isSubmitting: [isSubmitting],
									methods: [methods],
									errors: [errors],
									onBackButton: [onBackButton],
									pageCurrentPage: [pageCurrentPage],
									onClick: [onHandleSecondPerson],
									showSecond: [secondPersonAdded],
									shouldDisableDoublePayment: [shouldDisableDoublePayment],
									validateTrauma: [
										(val, person) => onHandleValidateTrauma(val, person),
									],
									validateOccupation: [
										(val, person) => onHandleValidateOccupation(val, person),
									],
									validateOutcomeTrauma: [
										(val) => onHandleValidateOutcomeTrauma(val),
									],
									handleAddressEntryType: [
										(name, val) => onHandleMailingAddress(name, val),
									],
									handlePaymentMethodType: [
										(name, val) => onHandlePaymentMethodType(name, val),
									],
									handleCCUpgradePolicy: [
										(val) => onHandleCCUpgradePolicy(val),
									],
									handleApplyCheckbox: [(val) => onHandleApplyCheckbox(val)],
									handleAdditionalPolicyHolder: [
										(name, val) => onHandleAdditionalPolicyHolder(name, val),
									],
									showAdditionalPolicyHolder: [additionalPolicyHolder],
									isAdditionalSameAsPrimary: [isAdditionalSameAsPrimary],
								}}
							/>
						) : null}
					</>
				</MainLayout>
			</FormProvider>
			<Footer
				logoImage="https://www.allianz.com/content/dam/onemarketing/system/allianz-logo.svg"
				footerImageOne="https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/footer-image/life-customer.jpg"
				footerImageTwo="https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/footer-image/life-ins-code.jpg"
				termOfUseLink="https://www.allianz.com.au/terms-of-use.html"
				privacyLink="https://www.allianz.com.au/privacy-policy.html"
				securityLink="https://www.allianz.com.au/security.html"
				disclaimerLink="https://www.allianz.com.au/disclaimer.html"
				showQuoteSummaryDisclaimer={
					pageCurrentPage?.identifier === pageName.QUOTE
				}
				showPostQuoteDisclaimer={
					!isQuotePages(pageCurrentPage?.identifier) && !isContactCenter
				}
			/>
		</>
	);
}
